import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const SecurityPoliciesTab = () => {
  const [securitySettings, setSecuritySettings] = useState({
    mfaRequired: true,
    mfaRoles: ['admin', 'operator'],
    sessionExpiration: 480, // minutes
    deviceRemembering: true,
    deviceRememberDays: 30,
    auditRetention: 365, // days
    tlsRequired: true,
    passwordComplexity: true,
    loginAttempts: 5,
    lockoutDuration: 30 // minutes
  });

  const [hasChanges, setHasChanges] = useState(false);

  const sessionOptions = [
    { value: 60, label: '1 hora' },
    { value: 120, label: '2 horas' },
    { value: 240, label: '4 horas' },
    { value: 480, label: '8 horas' },
    { value: 720, label: '12 horas' },
    { value: 1440, label: '24 horas' }
  ];

  const retentionOptions = [
    { value: 90, label: '90 días' },
    { value: 180, label: '6 meses' },
    { value: 365, label: '1 año' },
    { value: 730, label: '2 años' },
    { value: 1095, label: '3 años' },
    { value: 2555, label: '7 años (Cumplimiento)' }
  ];

  const handleSettingChange = (key, value) => {
    setSecuritySettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleRoleToggle = (role) => {
    const currentRoles = securitySettings?.mfaRoles;
    const newRoles = currentRoles?.includes(role)
      ? currentRoles?.filter(r => r !== role)
      : [...currentRoles, role];
    
    handleSettingChange('mfaRoles', newRoles);
  };

  const handleSave = () => {
    console.log('Guardando configuración de seguridad:', securitySettings);
    setHasChanges(false);
  };

  const handleReset = () => {
    setSecuritySettings({
      mfaRequired: true,
      mfaRoles: ['admin', 'operator'],
      sessionExpiration: 480,
      deviceRemembering: true,
      deviceRememberDays: 30,
      auditRetention: 365,
      tlsRequired: true,
      passwordComplexity: true,
      loginAttempts: 5,
      lockoutDuration: 30
    });
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-red-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-red-900">Políticas de Seguridad</h4>
            <p className="text-sm text-red-700 mt-1">
              Configure las políticas de seguridad globales del sistema. Los cambios afectan a todos los usuarios y requieren confirmación administrativa.
            </p>
          </div>
        </div>
      </div>
      {/* Multi-Factor Authentication */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-foreground">Autenticación Multifactor (MFA)</h3>
            <p className="text-sm text-muted-foreground">
              Configurar requisitos de autenticación de dos factores
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-muted-foreground">
              {securitySettings?.mfaRequired ? 'Requerido' : 'Opcional'}
            </span>
            <button
              onClick={() => handleSettingChange('mfaRequired', !securitySettings?.mfaRequired)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings?.mfaRequired ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings?.mfaRequired ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {securitySettings?.mfaRequired && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-3 block">
                Roles que requieren MFA
              </label>
              <div className="space-y-2">
                {[
                  { key: 'admin', label: 'Administradores', description: 'Acceso completo al sistema' },
                  { key: 'operator', label: 'Operadores', description: 'Consultas VIN y operaciones diarias' },
                  { key: 'auditor', label: 'Auditores', description: 'Acceso de solo lectura' }
                ]?.map((role) => (
                  <Checkbox
                    key={role?.key}
                    label={
                      <div>
                        <div className="font-medium">{role?.label}</div>
                        <div className="text-sm text-muted-foreground">{role?.description}</div>
                      </div>
                    }
                    checked={securitySettings?.mfaRoles?.includes(role?.key)}
                    onChange={() => handleRoleToggle(role?.key)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Session Management */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Gestión de Sesiones</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          <Select
            label="Expiración de Sesión"
            description="Tiempo antes de cerrar sesión automáticamente"
            options={sessionOptions}
            value={securitySettings?.sessionExpiration}
            onChange={(value) => handleSettingChange('sessionExpiration', value)}
          />

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">Recordar Dispositivos</div>
                <div className="text-sm text-muted-foreground">
                  Permitir que los usuarios marquen dispositivos como confiables
                </div>
              </div>
              <button
                onClick={() => handleSettingChange('deviceRemembering', !securitySettings?.deviceRemembering)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  securitySettings?.deviceRemembering ? 'bg-primary' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    securitySettings?.deviceRemembering ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {securitySettings?.deviceRemembering && (
              <Input
                label="Días para Recordar Dispositivo"
                type="number"
                value={securitySettings?.deviceRememberDays}
                onChange={(e) => handleSettingChange('deviceRememberDays', parseInt(e?.target?.value))}
                min="1"
                max="365"
              />
            )}
          </div>
        </div>
      </div>
      {/* Security Controls */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Controles de Seguridad</h3>
        
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-foreground">Requerir TLS/HTTPS</div>
              <div className="text-sm text-muted-foreground">
                Forzar conexiones seguras para todas las comunicaciones
              </div>
            </div>
            <button
              onClick={() => handleSettingChange('tlsRequired', !securitySettings?.tlsRequired)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings?.tlsRequired ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings?.tlsRequired ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-foreground">Complejidad de Contraseñas</div>
              <div className="text-sm text-muted-foreground">
                Requerir contraseñas complejas (8+ caracteres, mayúsculas, números, símbolos)
              </div>
            </div>
            <button
              onClick={() => handleSettingChange('passwordComplexity', !securitySettings?.passwordComplexity)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                securitySettings?.passwordComplexity ? 'bg-primary' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  securitySettings?.passwordComplexity ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Intentos de Login Máximos"
              type="number"
              value={securitySettings?.loginAttempts}
              onChange={(e) => handleSettingChange('loginAttempts', parseInt(e?.target?.value))}
              min="3"
              max="10"
              description="Número de intentos antes de bloquear cuenta"
            />

            <Input
              label="Duración de Bloqueo (minutos)"
              type="number"
              value={securitySettings?.lockoutDuration}
              onChange={(e) => handleSettingChange('lockoutDuration', parseInt(e?.target?.value))}
              min="5"
              max="1440"
              description="Tiempo de bloqueo después de intentos fallidos"
            />
          </div>
        </div>
      </div>
      {/* Audit and Compliance */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-4">Auditoría y Cumplimiento</h3>
        
        <Select
          label="Retención de Registros de Auditoría"
          description="Tiempo de conservación de logs y registros de actividad"
          options={retentionOptions}
          value={securitySettings?.auditRetention}
          onChange={(value) => handleSettingChange('auditRetention', value)}
        />

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
            <div>
              <h5 className="font-medium text-blue-900">Cumplimiento Regulatorio</h5>
              <p className="text-sm text-blue-700 mt-1">
                La retención de 7 años cumple con los requisitos de la Ley Federal de Protección de Datos Personales y regulaciones automotrices mexicanas.
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Security Status */}
      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} className="text-green-600" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">MFA Activo</h4>
              <p className="text-sm text-muted-foreground">Usuarios protegidos</p>
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground">85%</div>
          <div className="text-sm text-muted-foreground">de usuarios activos</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">Sesiones Activas</h4>
              <p className="text-sm text-muted-foreground">Usuarios conectados</p>
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground">23</div>
          <div className="text-sm text-muted-foreground">sesiones válidas</div>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-red-600" />
            </div>
            <div>
              <h4 className="font-semibold text-foreground">Intentos Bloqueados</h4>
              <p className="text-sm text-muted-foreground">Últimas 24 horas</p>
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground">7</div>
          <div className="text-sm text-muted-foreground">intentos fallidos</div>
        </div>
      </div>
      {hasChanges && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="AlertCircle" size={20} className="text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              Tienes cambios sin guardar en las políticas de seguridad
            </span>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" size="sm" onClick={handleReset}>
              Descartar
            </Button>
            <Button variant="default" size="sm" onClick={handleSave}>
              Guardar Cambios
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityPoliciesTab;